import { useMemo } from "react"
import { useQuery } from "urql"

import type {
  GetWarehouseShelvingLookupListQuery,
  WarehouseShelving,
  WarehouseShelvingBinSize,
  WarehouseShelvingQueryListArgs,
} from "@/graphql/codegen/graphql"
import { GetWarehouseShelvingLookupListDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { Option } from "@/types/form-fields"
import { isNil } from "@/utils/utils"

interface UseGetInventoryShelvingOptionsParams {
  warehouseAreaId?: string
  binSize?: WarehouseShelvingBinSize | null
  aisleCode?: string | null
  sectionCode?: string | null
  warehouseShelvingId?: string | null
  selectedShelvingIds?: string[]
  includeUnavailable?: boolean
}

interface UseGetInventoryShelvingOptionsReturn {
  aisleOptions: Option<string>[]
  shelfOptions: Option<string>[]
  binOptions: Option<number>[]
  shelvings: WarehouseShelving[]
  availableShelvings: WarehouseShelving[]
  isFetching: boolean
  isAisleFull: boolean
  isSectionFull: boolean
}

const LIMIT = 10000

export const useGetInventoryShelvingOptions = ({
  warehouseAreaId,
  binSize,
  aisleCode,
  sectionCode,
  selectedShelvingIds,
  warehouseShelvingId,
  includeUnavailable,
}: UseGetInventoryShelvingOptionsParams): UseGetInventoryShelvingOptionsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ data, fetching }] = useQuery<GetWarehouseShelvingLookupListQuery, WarehouseShelvingQueryListArgs>({
    query: GetWarehouseShelvingLookupListDocument,
    variables: {
      input: {
        filter: {
          warehouseAreaId,
          isAvailable: includeUnavailable ? undefined : true,
          isDoNotUse: false,
          binSize: binSize ? [binSize] : undefined,
        },
        limit: LIMIT,
        offset: 0,
      },
    },
    pause: !warehouseAreaId,
  })

  const shelvings = useMemo(() => {
    if (data?.warehouseShelving.list.__typename === "WarehouseShelvingListFailure") {
      notify({
        type: NotificationType.Error,
        message: data.warehouseShelving.list.error.message ?? "Failed to fetch warehouse shelving.",
      })
      return []
    }

    return (data?.warehouseShelving.list.warehouseShelvings ?? []) as WarehouseShelving[]
  }, [data, notify])

  const availableShelvings = useMemo(() => {
    const takenIds = new Set<string>(selectedShelvingIds)

    if (warehouseShelvingId) takenIds.delete(warehouseShelvingId)

    if (!takenIds.size) return shelvings

    return shelvings.filter((shelving) => !takenIds.has(shelving.warehouseShelvingId))
  }, [shelvings, selectedShelvingIds, warehouseShelvingId])

  const aisleOptions = useMemo<Option<string>[]>(() => {
    const uniqueAisleCodes = new Set<string>()

    for (const shelving of availableShelvings) {
      if (shelving.aisleCode) {
        uniqueAisleCodes.add(shelving.aisleCode)
      }
    }

    if (aisleCode) uniqueAisleCodes.add(aisleCode)

    return Array.from(uniqueAisleCodes)
      .sort((a, b) => {
        const aNum = Number.parseInt(a, 10)
        const bNum = Number.parseInt(b, 10)
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return aNum - bNum
        }
        return a.localeCompare(b)
      })
      .map((aisle) => ({ value: aisle, label: aisle }))
  }, [availableShelvings, aisleCode])

  const shelfOptions = useMemo<Option<string>[]>(() => {
    if (!aisleCode) return []

    const uniqueSectionCodes = new Set<string>()

    for (const shelving of availableShelvings) {
      if (shelving.aisleCode === aisleCode && shelving.sectionCode) {
        uniqueSectionCodes.add(shelving.sectionCode)
      }
    }

    if (sectionCode) uniqueSectionCodes.add(sectionCode)

    return Array.from(uniqueSectionCodes)
      .sort((a, b) => a.localeCompare(b))
      .map((shelf) => ({ value: shelf, label: shelf }))
  }, [availableShelvings, aisleCode, sectionCode])

  const binOptions = useMemo<Option<number>[]>(() => {
    if (!aisleCode || !sectionCode) return []

    const uniqueBinNumbers = new Set<number>()

    for (const shelving of availableShelvings) {
      if (shelving.aisleCode === aisleCode && shelving.sectionCode === sectionCode && !isNil(shelving.binNumber)) {
        uniqueBinNumbers.add(shelving.binNumber)
      }
    }

    return Array.from(uniqueBinNumbers)
      .sort((a, b) => a - b)
      .map((bin) => ({ value: bin, label: bin.toString() }))
  }, [availableShelvings, aisleCode, sectionCode])

  const isAisleFull = !fetching && !!aisleCode && shelfOptions.length === 0
  const isSectionFull = !fetching && !!sectionCode && binOptions.length === 0

  return {
    aisleOptions,
    shelfOptions,
    binOptions,
    shelvings,
    availableShelvings,
    isFetching: fetching,
    isAisleFull,
    isSectionFull,
  }
}
